import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress
} from '@mui/material';
import { Add, Edit, Delete, Close, Refresh, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getCurrencyRates, createCurrencyRate, updateCurrencyRate, deleteCurrencyRate } from '../../services/currency';
import { useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';

const DEFAULT_BASE = {
  base_currency: 'Naira',
  base_currency_symbol: '₦',
  base_currency_code: 'NGN',
  base_currency_rate: 220,
};

const CURRENCIES = [
  { code: 'NGN', label: 'Naira', symbol: '₦', rate: 220 },
  { code: 'CNY', label: 'Yuan', symbol: '¥', rate: 1 },
  { code: 'USD', label: 'US Dollar', symbol: '$', rate: 1 },
];

const SYMBOLS = [
  { symbol: '₦', code: 'NGN', label: 'Naira' },
  { symbol: '¥', code: 'CNY', label: 'Chinese Yuan' },
  { symbol: '$', code: 'USD', label: 'USD' },
];
const CODES = [
  { code: 'NGN', label: 'Naira' },
  { code: 'CNY', label: 'Chinese Yuan' },
  { code: 'USD', label: 'USD' },
];

const CurrencyRates = () => {
  const { t } = useTranslation(['currencyRates', 'common']);
  const user = useSelector(state => state.auth.user);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    ...DEFAULT_BASE,
    target_currency: '',
    target_currency_symbol: '',
    target_currency_code: '',
    target_currency_rate: '',
  });
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const totalPages = Math.ceil(total / rowsPerPage) || 1;

  const fetchRates = async (pageArg = page, rowsArg = rowsPerPage) => {
    setLoading(true);
    try {
      const data = await getCurrencyRates({ page: pageArg + 1, page_size: rowsArg });
      setRates(data.results || []);
      setTotal(data.count || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRates(page, rowsPerPage);
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  const handlePrevPage = () => { if (page > 0) setPage(page - 1); };
  const handleNextPage = () => { if (page < totalPages - 1) setPage(page + 1); };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => { setCustomRowsInput(e.target.value.replace(/\D/g, '')); };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) { setRowsPerPage(val); setPage(0); }
    setCustomRowsActive(false);
  };
  const handleRefresh = () => { setPage(0); fetchRates(0, rowsPerPage); };

  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const openAdd = () => {
    setEditId(null);
    setForm({
      ...DEFAULT_BASE,
      target_currency: '',
      target_currency_symbol: '',
      target_currency_code: '',
      target_currency_rate: '',
    });
    setModalOpen(true);
  };

  const openEdit = (rate) => {
    setEditId(rate.id);
    setForm({
      base_currency: rate.base_currency,
      base_currency_symbol: rate.base_currency_symbol,
      base_currency_code: rate.base_currency_code,
      base_currency_rate: rate.base_currency_rate,
      target_currency: rate.target_currency,
      target_currency_symbol: rate.target_currency_symbol,
      target_currency_code: rate.target_currency_code,
      target_currency_rate: rate.target_currency_rate,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('currencyRates:messages.deleteConfirm'))) {
      await deleteCurrencyRate(id);
      fetchRates();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editId) {
        await updateCurrencyRate(editId, payload);
      } else {
        await createCurrencyRate(payload);
      }
      setModalOpen(false);
      fetchRates();
    } finally {
      setSaving(false);
    }
  };

  const formatRate = (rate) => {
    if (rate === null || rate === undefined) return '';
    const num = Number(rate);
    if (Number.isNaN(num)) return '';
    if (Number.isInteger(num)) return num.toString();
    return num % 1 === 0 ? num.toString() : num.toString().replace(/\.0+$/, '').replace(/(\.[1-9]*)0+$/, '$1');
  };

  // Only superusers/admins can access
  if (!user?.is_superuser && !user?.is_admin) {
    return <Box p={4}><Typography color="error">{t('currencyRates:accessDenied')}</Typography></Box>;
  }

  const rowsForGrid = addSerialNumbers(rates, page, rowsPerPage);
  const columns = [
    { field: 'sn', headerName: t('currencyRates:table.sn'), width: 80, sortable: false, filterable: false },
    { field: 'base_currency_symbol', headerName: t('currencyRates:table.baseSymbol'), width: 100 },
    { field: 'base_currency_code', headerName: t('currencyRates:table.baseCode'), width: 100 },
    { field: 'base_currency_rate', headerName: t('currencyRates:table.baseRate'), width: 120, renderCell: (params) => formatRate(params.row?.base_currency_rate) },
    { field: 'target_currency_symbol', headerName: t('currencyRates:table.targetSymbol'), width: 100 },
    { field: 'target_currency_code', headerName: t('currencyRates:table.targetCode'), width: 100 },
    { field: 'target_currency_rate', headerName: t('currencyRates:table.targetRate'), width: 120, renderCell: (params) => formatRate(params.row?.target_currency_rate) },
    { field: 'created_by', headerName: t('currencyRates:table.createdBy'), width: 160, renderCell: (params) => {
      return params.row?.created_by?.username || '';
    } },
    { field: 'created_at', headerName: t('currencyRates:table.date'), flex: 1, renderCell: (params) => {
      if (params.row && params.row.created_at) {
        const d = new Date(params.row.created_at);
        const pad = n => n.toString().padStart(2, '0');
        return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
      return '';
    } },
    {
      field: 'actions',
      headerName: t('currencyRates:table.actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => openEdit(params.row)}><Edit /></IconButton>
          <IconButton color="error" onClick={() => handleDelete(params.row.id)}><Delete /></IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('currencyRates:title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAdd}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
          >
            {t('currencyRates:addRate')}
          </Button>
          <IconButton onClick={handleRefresh}><Refresh /></IconButton>
        </Box>
      </Box>
      <Box sx={{
        width: '100%',
        overflowX: 'auto',
        minHeight: 520,
        '&::-webkit-scrollbar': {
          height: 8,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
      }}>
        <DataGrid
          rows={rowsForGrid}
          columns={columns}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          pageSize={rowsPerPage}
          onPageSizeChange={(newPageSize) => { setRowsPerPage(newPageSize); setPage(0); }}
          pagination
          rowCount={total}
          paginationMode="server"
          rowsPerPageOptions={[10, 20, 50, 100]}
          disableRowSelectionOnClick
          loading={loading}
          hideFooter
          sx={{
            border: 'none',
            minWidth: 1200, // Ensure minimum width for all columns
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#DC143C',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            },
            '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
              color: 'white'
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#FFF0F3'
            },
            '& .MuiDataGrid-cell': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
              padding: { xs: '8px 4px', sm: '8px 12px' }
            }
          }}
        />
      </Box>
      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}><ChevronLeft /></IconButton>
          <Typography variant="body2">{t('currencyRates:pagination.page', { current: totalPages === 0 ? 0 : page + 1, total: totalPages })}</Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}><ChevronRight /></IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{t('currencyRates:pagination.rowsPerPage')}</Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('currencyRates:pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('currencyRates:pagination.rows')}
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editId ? t('currencyRates:dialogs.editTitle') : t('currencyRates:dialogs.addTitle')}
          <IconButton onClick={() => setModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Remove Base Code and Target Code fields */}
          <TextField
            select
            label={t('currencyRates:dialogs.baseSymbol')}
            value={form.base_currency_symbol}
            onChange={e => {
              const selected = SYMBOLS.find(opt => opt.symbol === e.target.value);
              setForm(f => ({
                ...f,
                base_currency_symbol: selected.symbol,
                base_currency_code: selected.code,
                base_currency: selected.label // Set the name
              }));
            }}
            fullWidth
            margin="normal"
          >
            {SYMBOLS.map(opt => (
              <MenuItem key={opt.symbol} value={opt.symbol}>{opt.label} ({opt.symbol})</MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('currencyRates:dialogs.baseRate')}
            type="number"
            value={form.base_currency_rate}
            onChange={e => setForm(f => ({ ...f, base_currency_rate: e.target.value }))}
            fullWidth
            margin="normal"
            inputProps={{ step: 'any' }}
          />
          <TextField
            select
            label={t('currencyRates:dialogs.targetSymbol')}
            value={form.target_currency_symbol}
            onChange={e => {
              const selected = SYMBOLS.find(opt => opt.symbol === e.target.value);
              setForm(f => ({
                ...f,
                target_currency_symbol: selected.symbol,
                target_currency_code: selected.code,
                target_currency: selected.label // Set the name
              }));
            }}
            fullWidth
            margin="normal"
          >
            {SYMBOLS.map(opt => (
              <MenuItem key={opt.symbol} value={opt.symbol}>{opt.label} ({opt.symbol})</MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('currencyRates:dialogs.targetRate')}
            type="number"
            value={form.target_currency_rate}
            onChange={e => setForm(f => ({ ...f, target_currency_rate: e.target.value }))}
            fullWidth
            margin="normal"
            inputProps={{ step: 'any' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="inherit">{t('currencyRates:dialogs.cancel')}</Button>
          <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : t('currencyRates:dialogs.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CurrencyRates; 